import { getDefaultBoxWidth, search_asp } from "../util";

const projectionDefaultWidth = {
  mercator: 630,
  transverseMercator: 495,
  albers: 540,
  albersUSA: 90,
  azimuthalEqualArea: 400,
  azimuthalEquidistant: 520,
  conicConformal: 720,
  conicEqualArea: 630,
  conicEquidistant: 735,
  equalEarth: 500,
  equirectangular: 640,
  gnomonic: 350,
  identity: 1,
  orthographic: 200,
  stereographic: 590
}
const mercatorTransposedHeight = 630;

function getProjectionResizingRuleAfterResizing(rule_resizing, source, transposed) {
  let rule_map_rescale = {
    category: "recommender",
    "description": "Rescale map",
    "specifier": {
      "role": "view"
    },
    "action": "modify",
    "option": {
      "projection": {}
    }
  };
  let ref_width1 = projectionDefaultWidth[source.layout.projection?.type || "mercator"];
  let ref_width2 = ref_width1;
  if (transposed) ref_width2 = projectionDefaultWidth.transverseMercator;
  let old_scale = source.layout.projection.scale;
  let old_scale_rate = source.layout?.width / ref_width1;
  let factor = old_scale / old_scale_rate;
  let new_scale = rule_resizing.option.width / ref_width2;
  let new_scale_rate = new_scale * factor;
  rule_map_rescale.option.projection.scale = new_scale_rate;
  return rule_map_rescale;
}

export function translateProportionateResizing(tech, args, line, parsed, source, mapping, processed, config) {
  let rules = [];
  let width = parseInt(args[0]);
  let height = Math.round(source.layout.height / source.layout.width * width);
  let rule_resizing = {
    category: "recommender",
    description: `Resize proportionately`,
    specifier: { "role": "view" },
    action: "modify",
    option: {
      width,
      height
    }
  }
  let rule_map_rescale
  if (search_asp(parsed, "composition(projection)").length == 1 && search_asp(parsed, "do(map_to_", "startsWith").length == 0) {
    // change width and height (only for map)
    let transposed = false;
    if (search_asp(parsed, "do(transpose)").length == 1) {
      let new_height = Math.round(Math.pow(rule_resizing.option.width, 2) / rule_resizing.option.height);
      rule_resizing.option.height = new_height;
      if (source.layout.projection.type === "mercator") {
        rule_resizing.option.height = Math.round(rule_resizing.option.width / projectionDefaultWidth.transverseMercator * mercatorTransposedHeight);
      }
      transposed = true;
    }
    rule_map_rescale = getProjectionResizingRuleAfterResizing(rule_resizing, source, transposed);

  }
  rules.push(rule_resizing);
  if (rule_map_rescale) {
    rules.push(rule_map_rescale);
  }
  let padding = {};
  let be_transposed = (search_asp(parsed, "do(transpose", "startsWith").length == 1);
  if (search_asp(parsed, "do(adjust_view_padding)").length == 1) {
    if (search_asp(parsed, "do(remove_view_padding,", "startsWith").length == 1) {
      padding = { left: 0, right: 0, top: 0, bottom: 0 };
    } else {
      let hFactor = width / source.layout.width;
      let vFactor = height / source.layout.height;
      if (hFactor && source.layout.padding?.left) padding.left = source.layout.padding.left * hFactor;
      if (hFactor && source.layout.padding?.right) padding.right = source.layout.padding.right * hFactor;
      if (vFactor && source.layout.padding?.top) padding.top = source.layout.padding.top * vFactor;
      if (vFactor && source.layout.padding?.bottom) padding.bottom = source.layout.padding.bottom * vFactor;
    }
    let rule_padding_adjust = {
      category: "recommender",
      description: `Adjust padding proportionately`,
      specifier: { "role": "view" },
      action: "modify",
      option: {
        padding
      }
    }
    rules.push(rule_padding_adjust);
  }

  // axis offset adjustment
  if (source.layout?.haxis?.offset !== undefined && source.layout?.haxis?.offset != 0) {
    let original_content_height = source.layout.height - (source.layout.padding?.top || 0) - (source.layout.padding?.bottom || 0);
    let new_content_height = height - (padding?.top || 0) - (padding?.bottom || 0);
    if (be_transposed) {
      new_content_height = width - (padding?.left || 0) - (padding?.right || 0);
    }
    if (original_content_height != new_content_height) {
      let new_offset = source.layout.haxis.offset * new_content_height / original_content_height;
      rules.push({
        category: "recommender",
        description: `Adjust hAxis offset`,
        specifier: { "role": "view.hAxis" },
        action: "modify",
        option: {
          offset: new_offset
        }
      });
    }
  }
  if (source.layout?.vaxis?.offset !== undefined && source.layout?.vaxis?.offset != 0) {
    let original_content_width = source.layout.width - (source.layout.padding?.left || 0) - (source.layout.padding?.right || 0);
    let new_content_width = width - (padding?.left || 0) - (padding?.right || 0);
    if (be_transposed) {
      new_content_width = height - (padding?.top || 0) - (padding?.bottom || 0);
    }
    if (original_content_width != new_content_width) {
      let new_offset = source.layout.vaxis.offset * new_content_width / original_content_width;
      rules.push({
        category: "recommender",
        description: `Adjust vAxis offset`,
        specifier: { "role": "view.vAxis" },
        action: "modify",
        option: {
          offset: new_offset
        }
      });
    }
  }
  // data annotations reposition
  let be_ext_num = (search_asp(parsed, "do(externalize_data_annotations,with_numbering)").length == 1);
  source.annotation?.items?.forEach((anno, ni) => {
    // static
    if (anno.anchor?.dataItem && config.has_old_new_size) {
      let rule_reposition = {
        category: "recommender",
        description: `Reposition annotation for ${anno.name || ""}`,
        specifier: {
          "role": "annotation"
        },
        action: "modify",
        option: {
        }
      }
      if ("name" in anno) {
        rule_reposition.specifier.id = anno.name;
      }
      else {
        rule_reposition.specifier.index = ni;
      }
      // positioning
      let hFactor = !be_transposed ? config.new_size[0] / config.old_size[0] : config.new_size[0] / config.old_size[1];
      let vFactor = !be_transposed ? config.new_size[1] / config.old_size[1] : config.new_size[1] / config.old_size[0];
      let newFontSize = Math.max((anno.text[0].fontSize || 16) * vFactor, 12);
      let fontScale = newFontSize / anno.text[0].fontSize
      if (be_ext_num) {
        rule_reposition.option.width = Math.ceil((7.3 * (ni + 1)).toString().length * newFontSize / 12);
      } else if (anno.width) {
        rule_reposition.option.width = Math.min(anno.width, Math.round(anno.width * hFactor), Math.round(anno.width * fontScale));
      }
      rule_reposition.option.dx = Math.round(anno.dx * hFactor);
      rule_reposition.option.dy = Math.round(anno.dy * vFactor);

      // tick position
      if (anno.anchor?.callout === "yes") {
        rule_reposition.option.tick = { to: {} };
        rule_reposition.option.tick.to.dx = Math.round(anno.anchor.to.dx * hFactor);
        rule_reposition.option.tick.to.dy = Math.round(anno.anchor.to.dy * vFactor);
      }

      if (be_ext_num) {
        if (anno.anchor?.callout !== "yes") {
          rule_reposition.option.dx = Math.sign(anno.dx) * Math.abs(rule_reposition.option.width);
          rule_reposition.option.dy = Math.sign(anno.dy) * 10
        } else {
          let y_off = anno.anchor.to.dy - anno.anchor.from.dy, tick_dir;
          if (y_off < 0) tick_dir = "top";
          else if (y_off > 0) tick_dir = "bottom";
          let cornering_v = anno.anchor?.cornering ? anno.anchor?.cornering.split("-")[1] : "straight";
          if (tick_dir !== undefined && cornering_v === tick_dir) {
            // horizontal end
            rule_reposition.option.dx = Math.round(rule_reposition.option.tick.to.dx + (anno.anchor.to.dx < 0 ? -rule_reposition.option.width : Math.sign(anno.anchor.to.dx) * 10));
            rule_reposition.option.dy = Math.round(rule_reposition.option.tick.to.dy - Math.sign(anno.anchor.to.dy) * newFontSize / 4);
          } else if (tick_dir !== undefined && cornering_v !== tick_dir) {
            rule_reposition.option.dx = Math.round(rule_reposition.option.tick.to.dx);
            rule_reposition.option.dy = Math.round(rule_reposition.option.tick.to.dy + Math.sign(anno.anchor.to.dy) * (10 + (anno.anchor.to.dy < 0 ? newFontSize : 5)));
          } else {
            rule_reposition.option.dx = Math.round(rule_reposition.option.tick.to.dx + Math.sign(anno.anchor.to.dx) * 5);
            rule_reposition.option.dy = Math.round(rule_reposition.option.tick.to.dy + Math.sign(anno.anchor.to.dy) * 5);
          }
        }
      }

      rules.push(rule_reposition);
    }
  });
  return rules;
}

export function translateDisproportionateResizing(tech, args, line, parsed, source, mapping, processed, config) {
  let rules = [];
  let height;
  let width = parseInt(args[0]);
  let is_to_heatmap = search_asp(parsed, "do(make_heatmap", "startsWith")[0];
  if (args[1] === "repeated" && !is_to_heatmap) {
    let n1 = source.datatype[source.layout?.split?.field]?.cardinality;
    let n2 = source.layout?.split?.values?.length
    let n_facets = Math.min(n1 || Infinity, n2 || Infinity);
    if (n_facets > 0 && n_facets !== Infinity) {
      let n_rows = Math.ceil(n_facets / source.layout?.nColumns);
      let n_cols = source.layout?.nColumns;
      let height_unit = source.layout.height / n_rows;
      let p;
      if ((p = search_asp(parsed, "do(change_n_facet_columns,", "startsWith")[0], p)) {
        let n_new_cols = parseInt(p.args[1]);
        let size_factor = (source.layout.width / n_cols) / (width / n_new_cols);
        let n_new_rows = Math.ceil(n_facets / n_new_cols);
        height = Math.min(height_unit / size_factor * n_new_rows, height_unit * n_new_rows);
      } else {
        let size_factor = source.layout.width / width;
        height = Math.min(height_unit / size_factor * n_rows, height_unit * n_rows);
      }
    }
  } else {
    height = parseInt(args[1])
  };

  let rule_resizing = {
    category: "recommender",
    description: `Resize disproportionately`,
    specifier: { "role": "view" },
    action: "modify",
    option: {
      width,
      height
    }
  }
  rules.push(rule_resizing);

  let be_transposed = (search_asp(parsed, "do(transpose", "startsWith").length == 1);
  if (search_asp(parsed, "composition(projection).").length == 1 && search_asp(parsed, "do(map_to_", "startsWith").length == 0) {
    let rule_map_rescale = getProjectionResizingRuleAfterResizing(rule_resizing, source);
    rules.push(rule_map_rescale);
  }
  let padding = {};
  if (search_asp(parsed, "do(adjust_view_padding)").length == 1) {
    if (search_asp(parsed, "do(remove_view_padding,", "startsWith").length == 1) {
      padding = { left: 0, right: 0, top: 0, bottom: 0 };
    } else {
      let hFactor = width / source.layout.width;
      let vFactor = height / source.layout.height;
      if (hFactor && source.layout.padding?.left) padding.left = Math.round(source.layout.padding.left * hFactor);
      if (hFactor && source.layout.padding?.right) padding.right = Math.round(source.layout.padding.right * hFactor);
      if (vFactor && source.layout.padding?.top) padding.top = Math.round(source.layout.padding.top * vFactor);
      if (vFactor && source.layout.padding?.bottom) padding.bottom = Math.round(source.layout.padding.bottom * vFactor);
      if (be_transposed) {
        padding = {
          top: padding.left,
          left: padding.bottom,
          bottom: padding.right,
          right: padding.top
        };
      }
    }
    let rule_padding_adjust = {
      category: "recommender",
      description: `Adjust padding proportionately`,
      specifier: { "role": "view" },
      action: "modify",
      option: {
        padding
      }
    }
    rules.push(rule_padding_adjust);
  }
  // axis offset adjustment
  if (source.layout?.haxis?.offset !== undefined && source.layout?.haxis?.offset != 0) {
    let original_content_height = source.layout.height - (source.layout.padding?.top || 0) - (source.layout.padding?.bottom || 0);
    let new_content_height = height - (padding?.top || 0) - (padding?.bottom || 0);
    if (be_transposed) {
      new_content_height = width - (padding?.left || 0) - (padding?.right || 0);
    }
    if (original_content_height != new_content_height) {
      let new_offset = source.layout.haxis.offset * new_content_height / original_content_height;
      rules.push({
        category: "recommender",
        description: `Adjust hAxis offset`,
        specifier: { "role": "view.hAxis" },
        action: "modify",
        option: {
          offset: new_offset
        }
      });
    }
  }
  if (source.layout?.vaxis?.offset !== undefined && source.layout?.vaxis?.offset != 0) {
    let original_content_width = source.layout.width - (source.layout.padding?.left || 0) - (source.layout.padding?.right || 0);
    let new_content_width = width - (padding?.left || 0) - (padding?.right || 0);
    if (be_transposed) {
      new_content_width = height - (padding?.top || 0) - (padding?.bottom || 0);
    }
    if (original_content_width != new_content_width) {
      let new_offset = source.layout.vaxis.offset * new_content_width / original_content_width;
      rules.push({
        category: "recommender",
        description: `Adjust vAxis offset`,
        specifier: { "role": "view.vAxis" },
        action: "modify",
        option: {
          offset: new_offset
        }
      });
    }
  }
  // data annotations reposition
  let be_ext_num = (search_asp(parsed, "do(externalize_data_annotations,with_numbering)").length == 1);
  let no_annotation_changes = (search_asp(parsed, "detect(prevent_annotation_changes)").length == 1);
  if (!no_annotation_changes) {
    source.annotation?.items?.forEach((anno, ni) => {
      // static
      if (anno.anchor?.dataItem && config.has_old_new_size) {
        let rule_reposition = {
          category: "recommender",
          description: `Reposition annotation for ${anno.name || ""}`,
          specifier: {
            "role": "annotation"
          },
          action: "modify",
          option: {
          }
        }
        if ("name" in anno) {
          rule_reposition.specifier.id = anno.name;
        }
        else {
          rule_reposition.specifier.index = ni;
        }
        // positioning
        let hFactor = !be_transposed ? config.new_size[0] / config.old_size[0] : config.new_size[0] / config.old_size[1];
        let vFactor = !be_transposed ? config.new_size[1] / config.old_size[1] : config.new_size[1] / config.old_size[0];
        let newFontSize = Math.max((anno.text[0].fontSize || 16) * vFactor, 12);
        let fontScale = newFontSize / (anno.text[0].fontSize || 16);
        if (be_ext_num) {
          rule_reposition.option.width = Math.ceil(7.3 * (ni + 1).toString().length * newFontSize / 12);
        } else if (anno.width) {
          rule_reposition.option.width = Math.min(anno.width, Math.round(anno.width * hFactor), Math.round(anno.width * fontScale));
        }
        rule_reposition.option.dx = Math.round(anno.dx * hFactor);
        rule_reposition.option.dy = Math.round(anno.dy * vFactor);
        
        if (!be_transposed) {
          if (anno.dx?.constructor.name === "String" && anno.dx.endsWith("%"))
            rule_reposition.option.dx = Math.round(anno.dx * hFactor);
          if (anno.dy?.constructor.name === "String" && anno.dy.endsWith("%"))
            rule_reposition.option.dy = Math.round(anno.dy * vFactor);
          // tick position
          if (anno.anchor?.callout === "yes") {
            let isDot = ["circle", "point", "square"].includes(anno.anchor.dataItem.role);
            rule_reposition.option.tick = { to: {} };
            rule_reposition.option.tick.to.dx = Math.round(anno.anchor.to.dx * hFactor * (isDot ? 0.75 : 0.9));
            if (Math.abs(anno.anchor.to.dx) > 10 && Math.abs(rule_reposition.option.tick.to.dx) < 10) {
              rule_reposition.option.tick.to.dx = Math.sign(anno.anchor.to.dx) * 10
            }
            rule_reposition.option.tick.to.dy = Math.round(anno.anchor.to.dy * vFactor * (isDot ? 0.75 : 0.9));
            if (Math.abs(anno.anchor.to.dy) > 10 && Math.abs(rule_reposition.option.tick.to.dy) < 10) {
              rule_reposition.option.tick.to.dy = Math.sign(anno.anchor.to.dy) * 10
            }
            if (!isDot && anno.anchor.from) {
              rule_reposition.option.tick.from = {};
              rule_reposition.option.tick.from.dx = Math.round(anno.anchor.from.dx * hFactor);
              rule_reposition.option.tick.from.dy = Math.round(anno.anchor.from.dy * vFactor);
            }
          }

          if (be_ext_num) {
            if (anno.anchor?.callout !== "yes") {
              rule_reposition.option.dx = Math.sign(anno.dx) * Math.abs(rule_reposition.option.width);
              rule_reposition.option.dy = Math.sign(anno.dy) * 10
            } else {
              let y_off = anno.anchor.to.dy - anno.anchor.from.dy, tick_dir;
              if (y_off < 0) tick_dir = "top";
              else if (y_off > 0) tick_dir = "bottom";
              let cornering_v = anno.anchor?.cornering ? anno.anchor?.cornering.split("-")[1] : "straight";
              if (tick_dir !== undefined && cornering_v === tick_dir) {
                // horizontal end
                rule_reposition.option.dx = Math.round(rule_reposition.option.tick.to.dx + (anno.anchor.to.dx < 0 ? -rule_reposition.option.width : Math.sign(anno.anchor.to.dx) * 10));
                rule_reposition.option.dy = Math.round(rule_reposition.option.tick.to.dy - Math.sign(anno.anchor.to.dy) * newFontSize / 4);
              } else if (tick_dir !== undefined && cornering_v !== tick_dir) {
                rule_reposition.option.dx = Math.round(rule_reposition.option.tick.to.dx);
                rule_reposition.option.dy = Math.round(rule_reposition.option.tick.to.dy + Math.sign(anno.anchor.to.dy) * (10 + (anno.anchor.to.dy < 0 ? newFontSize : 5)));
              } else {
                rule_reposition.option.dx = Math.round(rule_reposition.option.tick.to.dx + Math.sign(anno.anchor.to.dx) * 5);
                rule_reposition.option.dy = Math.round(rule_reposition.option.tick.to.dy + Math.sign(anno.anchor.to.dy) * 5);
              }
            }
          }
        } else {
          let org_dir = [(anno.dx < 0 ? "left" : (anno.dx == 0 ? "center" : "right")), (anno.dy < 0 ? "top" : (anno.dy == 0 ? "middle" : "bottom"))];
          let new_dir = [
            { top: "right", middle: "center", bottom: "left" }[org_dir[1]],
            { right: "bottom", center: "middle", left: "top" }[org_dir[0]]
          ];

          const gap = 15;
          if (new_dir[0] === "left") rule_reposition.option.dx = - gap * 2 - rule_reposition.option.width;
          else if (new_dir[0] === "center") rule_reposition.option.dx = 0;
          else if (new_dir[0] === "right") rule_reposition.option.dx = gap;

          let hypo_text_height;
          if (be_ext_num) {
            hypo_text_height = newFontSize;
          } else {
            hypo_text_height = anno.text.map((d) => {
              let pxLen = getDefaultBoxWidth(d.text, newFontSize) * 0.95;
              let lines = Math.ceil(pxLen / rule_reposition.option.width);
              pxLen - (lines - 1) * getDefaultBoxWidth(" ", newFontSize);
              lines = Math.ceil(pxLen / rule_reposition.option.width)
              return lines * newFontSize * (d.lineHeight || 160) / 100
            }).reduce((acc, cur) => acc + cur, 0);
          }

          if (new_dir[1] === "top") rule_reposition.option.dy = - gap - hypo_text_height;
          else if (new_dir[1] === "middle") rule_reposition.option.dy = 0;
          else if (new_dir[1] === "bottom") rule_reposition.option.dy = gap * 2;

          // position orient
          if (anno.anchor.positionOrient) {
            let old_po = anno.anchor.positionOrient.split("-");
            let new_po = [
              { right: "bottom", center: "middle", left: "top" }[old_po[1]],
              { top: "right", middle: "center", bottom: "left" }[old_po[0]]
            ];
            rule_reposition.option.orient = new_po.join("-");
          }

          // tick position
          if (anno.anchor.callout === "yes") {
            rule_reposition.option.tick = { to: {}, from: {} };
            let from = rotateByDeg([anno.anchor.from.dx, anno.anchor.from.dy], 90);
            let tick_orient = Math.abs(from[0]) > Math.abs(from[1]) ? "h" : "v";
            if (new_dir[0] === "left") {
              rule_reposition.option.tick.from.dx = tick_orient === "h" ? -10 : 0;
              rule_reposition.option.tick.to.dx = - gap - 10;
            } else if (new_dir[0] === "center") {
              rule_reposition.option.tick.from.dx = 0;
              rule_reposition.option.tick.to.dx = 0;
            } else if (new_dir[0] === "right") {
              rule_reposition.option.tick.from.dx = tick_orient === "h" ? 10 : 0;
              rule_reposition.option.tick.to.dx = 10 + gap;
            }
            if (new_dir[1] === "top") {
              rule_reposition.option.tick.from.dy = tick_orient === "h" ? 0 : -10;
              rule_reposition.option.tick.to.dy = - gap - 5;
            } else if (new_dir[1] === "center") {
              rule_reposition.option.tick.from.dy = 0;
              rule_reposition.option.tick.to.dy = 0;
            } else if (new_dir[1] === "bottom") {
              rule_reposition.option.tick.from.dy = tick_orient === "h" ? 0 : 10;
              rule_reposition.option.tick.to.dy = gap * 2;
            }


            if (anno.anchor.cornering) {
              rule_reposition.option.tick.cornering = {
                "right-top": "right-bottom", "right-bottom": "left-bottom", "left-bottom": "left-top", "left-top": "right-top"
              }[anno.anchor.cornering];
            }
            if (be_ext_num) {
              let y_off = rule_reposition.option.tick.to.dy - rule_reposition.option.tick.from.dy, tick_dir;
              if (y_off < 0) tick_dir = "top";
              else if (y_off > 0) tick_dir = "bottom";
              let cornering_v = rule_reposition.option.tick?.cornering ? rule_reposition.option.tick?.cornering.split("-")[1] : "straight";
              if (tick_dir !== undefined && cornering_v === tick_dir) {
                // horizontal end
                rule_reposition.option.dx = Math.round(rule_reposition.option.tick.to.dx + (rule_reposition.option.tick.to.dx < 0 ? -rule_reposition.option.width - 10 : Math.sign(rule_reposition.option.tick.to.dx) * 10));
                rule_reposition.option.dy = Math.round(rule_reposition.option.tick.to.dy - Math.sign(rule_reposition.option.tick.to.dy) * newFontSize / 4);
              } else if (tick_dir !== undefined && cornering_v !== tick_dir) {
                rule_reposition.option.dx = Math.round(rule_reposition.option.tick.to.dx - rule_reposition.option.width / 2);
                rule_reposition.option.dy = Math.round(rule_reposition.option.tick.to.dy + (rule_reposition.option.tick.to.dy < 0 ? -10 : 15));
              } else {
                rule_reposition.option.dx = Math.round(rule_reposition.option.tick.to.dx + Math.sign(rule_reposition.option.tick.to.dx) * 5);
                rule_reposition.option.dy = Math.round(rule_reposition.option.tick.to.dy + Math.sign(rule_reposition.option.tick.to.dy) * 5);
              }
            }

          }
        }
        rules.push(rule_reposition);
      }
    });
  }
  return rules;
}

export function translateIgnoreHeight(tech, args, line, parsed, source, mapping, processed, config) {
  let rules = [];
  rules.push({
    category: "recommender",
    description: `Ignore height`,
    specifier: { "role": "view" },
    action: "modify",
    option: {
      width: parseInt(args[0]),
      height: null
    }
  });
  return rules;
}

function rotateByDeg(coord, deg) {
  let x = coord[0], y = coord[1], rad = deg / 360 * (Math.PI * 2);
  return [x * cosine(rad) - y * sine(rad), x * sine(rad) + y * cosine(rad)];
}

function cosine(r) {
  return Math.round(Math.cos(r) * 100) / 100;
}

function sine(r) {
  return Math.round(Math.sin(r) * 100) / 100;
}